/**
 * 페이지(blocks 모드) → 1920×1080 안의 JSX 렌더.
 * SlideCanvas 의 자식으로 사용한다 — 부모가 1920×1080 컨테이너를 마련해둔 상태.
 *
 * layout 별 그리드 영역 결정 + blocks 를 순서대로 배치. 매우 간단한 분배 — 절대 픽셀.
 * (master-of-slide 의 자유로운 React 디자인은 mode='tsx' 페이지로 표현)
 */
import type { SlideAsset, SlideBlock, SlideDesign, SlidePage } from '@innermate/shared';
import { TsxRenderer } from './TsxRenderer';
import { Body, Bullets, Heading, Image, Kpi, Quote, Spacer } from './blocks';

interface Props {
  page: SlidePage;
  /** 자산 ID → URL 매핑. image 블록이 사용. */
  resolveAssetUrl?: (assetId: string) => string | undefined;
  /** mode='tsx' 페이지 sandbox 렌더에 사용할 디자인 토큰. (blocks 페이지는 부모 SlideCanvas 의 CSS vars 사용) */
  design?: SlideDesign;
  /** mode='tsx' 페이지의 useAsset(id) 헬퍼 lookup table. */
  assets?: SlideAsset[];
  /**
   * blocks 모드에서 block 클릭 시 호출. 메인 캔버스 인스펙터 연동용.
   * 썸네일/print 컨텍스트는 미전달 — read-only.
   */
  onSelectBlock?: (idx: number) => void;
  /** 강조 표시할 block 인덱스 (선택된 것). */
  selectedBlockIndex?: number | null;
}

const PADDING = 120; // 외곽 패딩 (px). master-of-slide 권장 100~160.

export function PageRenderer({
  page,
  resolveAssetUrl,
  design,
  assets,
  onSelectBlock,
  selectedBlockIndex,
}: Props) {
  if (page.content.mode === 'tsx') {
    if (!design) {
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            color: 'var(--osd-text)',
            opacity: 0.5,
            fontSize: 32,
          }}
        >
          TSX 페이지 (디자인 토큰 누락)
        </div>
      );
    }
    const map = new Map<string, string>();
    for (const a of assets ?? []) map.set(a.id, a.url);
    return <TsxRenderer tsxSource={page.content.tsxSource} design={design} assetUrlMap={map} />;
  }
  const { layout, blocks } = page.content;

  // 원본 blocks 배열의 인덱스를 유지해서 인스펙터 연동에 쓴다.
  const allIdx = blocks.map((_, i) => i);
  const idxOf = (b: SlideBlock) => blocks.indexOf(b);

  // layout 별 컨테이너 분할 + 정렬.
  switch (layout) {
    case 'hero':
      return (
        <CenterStack>
          <BlockList
            blocks={blocks}
            indices={allIdx}
            resolveAssetUrl={resolveAssetUrl}
            align="center"
            onSelectBlock={onSelectBlock}
            selectedIdx={selectedBlockIndex ?? null}
          />
        </CenterStack>
      );
    case 'two-column': {
      // 처음 절반 / 나머지 — 단순 2분할.
      const mid = Math.ceil(blocks.length / 2);
      return (
        <TwoCol
          left={blocks.slice(0, mid)}
          leftIndices={allIdx.slice(0, mid)}
          right={blocks.slice(mid)}
          rightIndices={allIdx.slice(mid)}
          resolveAssetUrl={resolveAssetUrl}
          onSelectBlock={onSelectBlock}
          selectedIdx={selectedBlockIndex ?? null}
        />
      );
    }
    case 'image-left':
    case 'image-right': {
      const imageBlock = blocks.find((b) => b.type === 'image');
      const others = blocks.filter((b) => b.type !== 'image');
      const swap = layout === 'image-right';
      const imageSide = imageBlock ? [imageBlock] : [];
      const imageIdx = imageBlock ? [idxOf(imageBlock)] : [];
      const othersIdx = others.map((b) => idxOf(b));
      return (
        <TwoCol
          left={swap ? others : imageSide}
          leftIndices={swap ? othersIdx : imageIdx}
          right={swap ? imageSide : others}
          rightIndices={swap ? imageIdx : othersIdx}
          resolveAssetUrl={resolveAssetUrl}
          onSelectBlock={onSelectBlock}
          selectedIdx={selectedBlockIndex ?? null}
        />
      );
    }
    case 'image-full': {
      const image = blocks.find((b) => b.type === 'image');
      const overlay = blocks.filter((b) => b.type !== 'image');
      const overlayIdx = overlay.map((b) => idxOf(b));
      return (
        <div style={{ position: 'absolute', inset: 0 }}>
          {image && image.type === 'image' && (
            <img
              src={resolveAssetUrl?.(image.assetId) ?? ''}
              alt={image.caption ?? ''}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: image.fit === 'contain' ? 'contain' : 'cover',
              }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              padding: PADDING,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: 24,
              background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.5) 100%)',
            }}
          >
            <BlockList
              blocks={overlay}
              indices={overlayIdx}
              resolveAssetUrl={resolveAssetUrl}
              onSelectBlock={onSelectBlock}
              selectedIdx={selectedBlockIndex ?? null}
            />
          </div>
        </div>
      );
    }
    case 'quote':
      return (
        <CenterStack>
          <BlockList
            blocks={blocks.length > 0 ? blocks : []}
            indices={allIdx}
            resolveAssetUrl={resolveAssetUrl}
            align="center"
            onSelectBlock={onSelectBlock}
            selectedIdx={selectedBlockIndex ?? null}
          />
        </CenterStack>
      );
    case 'kpi-row': {
      const kpis = blocks.filter((b) => b.type === 'kpi');
      const heading = blocks.find((b) => b.type === 'heading');
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: PADDING,
            display: 'flex',
            flexDirection: 'column',
            gap: 64,
            justifyContent: 'center',
          }}
        >
          {heading && (
            <Selectable
              originalIdx={idxOf(heading)}
              selectedIdx={selectedBlockIndex ?? null}
              onSelectBlock={onSelectBlock}
            >
              <RenderBlock block={heading} resolveAssetUrl={resolveAssetUrl} />
            </Selectable>
          )}
          <div style={{ display: 'flex', gap: 48, justifyContent: 'space-between' }}>
            {kpis.map((b) => {
              const oi = idxOf(b);
              return (
                <Selectable
                  key={oi}
                  originalIdx={oi}
                  selectedIdx={selectedBlockIndex ?? null}
                  onSelectBlock={onSelectBlock}
                  style={{ flex: 1 }}
                >
                  <RenderBlock block={b} resolveAssetUrl={resolveAssetUrl} />
                </Selectable>
              );
            })}
          </div>
        </div>
      );
    }
    default:
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: PADDING,
            display: 'flex',
            flexDirection: 'column',
            gap: 48,
            justifyContent: 'flex-start',
          }}
        >
          <BlockList
            blocks={blocks}
            indices={allIdx}
            resolveAssetUrl={resolveAssetUrl}
            onSelectBlock={onSelectBlock}
            selectedIdx={selectedBlockIndex ?? null}
          />
        </div>
      );
  }
}

/**
 * block 을 감싸 클릭 → onSelectBlock(originalIdx) 호출.
 * 메인 캔버스에서만 onSelectBlock 이 전달되며, 썸네일/print 컨텍스트는 read-only.
 */
function Selectable({
  originalIdx,
  selectedIdx,
  onSelectBlock,
  children,
  style,
}: {
  originalIdx: number;
  selectedIdx: number | null;
  onSelectBlock?: (idx: number) => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  if (!onSelectBlock) return <>{children}</>;
  const selected = selectedIdx === originalIdx;
  return (
    <div
      data-block-index={originalIdx}
      onClick={(e) => {
        e.stopPropagation();
        onSelectBlock(originalIdx);
      }}
      style={{
        cursor: 'pointer',
        outline: selected ? '2px solid var(--osd-accent, #3a80ff)' : 'none',
        outlineOffset: 6,
        borderRadius: 4,
        transition: 'outline-color 120ms',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CenterStack({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        padding: PADDING,
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}

function TwoCol({
  left,
  leftIndices,
  right,
  rightIndices,
  resolveAssetUrl,
  onSelectBlock,
  selectedIdx,
}: {
  left: SlideBlock[];
  leftIndices: number[];
  right: SlideBlock[];
  rightIndices: number[];
  resolveAssetUrl?: (assetId: string) => string | undefined;
  onSelectBlock?: (idx: number) => void;
  selectedIdx?: number | null;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        padding: PADDING,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 64,
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <BlockList
          blocks={left}
          indices={leftIndices}
          resolveAssetUrl={resolveAssetUrl}
          onSelectBlock={onSelectBlock}
          selectedIdx={selectedIdx ?? null}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <BlockList
          blocks={right}
          indices={rightIndices}
          resolveAssetUrl={resolveAssetUrl}
          onSelectBlock={onSelectBlock}
          selectedIdx={selectedIdx ?? null}
        />
      </div>
    </div>
  );
}

function BlockList({
  blocks,
  indices,
  resolveAssetUrl,
  align,
  onSelectBlock,
  selectedIdx,
}: {
  blocks: SlideBlock[];
  /** 원본 page.content.blocks 기준 인덱스 (blocks 와 평행). */
  indices: number[];
  resolveAssetUrl?: (assetId: string) => string | undefined;
  align?: 'left' | 'center' | 'right';
  onSelectBlock?: (idx: number) => void;
  selectedIdx?: number | null;
}) {
  return (
    <>
      {blocks.map((b, i) => {
        const oi = indices[i] ?? i;
        return (
          <Selectable
            key={oi}
            originalIdx={oi}
            selectedIdx={selectedIdx ?? null}
            onSelectBlock={onSelectBlock}
          >
            <RenderBlock block={b} resolveAssetUrl={resolveAssetUrl} forceAlign={align} />
          </Selectable>
        );
      })}
    </>
  );
}

function RenderBlock({
  block,
  resolveAssetUrl,
  forceAlign,
}: {
  block: SlideBlock;
  resolveAssetUrl?: (assetId: string) => string | undefined;
  forceAlign?: 'left' | 'center' | 'right';
}) {
  switch (block.type) {
    case 'heading':
      return <Heading {...block} align={forceAlign ?? block.align ?? 'left'} />;
    case 'body':
      return <Body {...block} align={forceAlign ?? block.align ?? 'left'} />;
    case 'bullets':
      return <Bullets {...block} />;
    case 'quote':
      return <Quote {...block} />;
    case 'image':
      return <Image {...block} src={resolveAssetUrl?.(block.assetId)} />;
    case 'kpi':
      return <Kpi {...block} />;
    case 'spacer':
      return <Spacer size={block.size ?? 'md'} />;
  }
}
